'use client'

import { useRouter } from 'next/navigation'
import type React from 'react'
import { RouterProvider } from 'react-aria-components'

import { config } from '@/shared/config'
import { ActiveLawProvider } from '@/shared/lib/cookieyes'

import { I18nProvider } from './i18n/provider'
import type { Language } from './i18n/types'
import { PublicIdProvider } from './public-id'
import { ReactQueryProvider } from './query'

// React Aria router integration for Next.js App Router
// See: https://react-spectrum.adobe.com/react-aria/routing.html#app-router
declare module 'react-aria-components' {
    interface RouterConfig {
        routerOptions: NonNullable<Parameters<ReturnType<typeof useRouter>['push']>[1]>
    }
}

type ProvidersProps = {
    lang: Language
    isLoggedIn: Promise<boolean>
}

function resolveHref(href: string) {
    // NOTE: Don't modify external links
    if (href.startsWith('http://') || href.startsWith('https://')) {
        return href
    }
    return config.basePath + href
}

export function Providers({ lang, isLoggedIn, children }: React.PropsWithChildren<ProvidersProps>) {
    const router = useRouter()
    const navigate = (href: string) => router.push(href as Parameters<typeof router.push>[0])

    return (
        <RouterProvider navigate={navigate} useHref={resolveHref}>
            <I18nProvider lang={lang}>
                <ActiveLawProvider>
                    <ReactQueryProvider>
                        <PublicIdProvider loggedIn={isLoggedIn}>{children}</PublicIdProvider>
                    </ReactQueryProvider>
                </ActiveLawProvider>
            </I18nProvider>
        </RouterProvider>
    )
}
